import {InAppNotificationAction} from "../../../types/notificationType";
import {useTranslation} from "react-i18next";
import {useEffect, useState} from "react";
import {shopModulesApi} from "../../../api/shopModulesApi";
import Autocomplete from "@mui/material/Autocomplete";
import {CircularProgress, Stack, TextField, Typography} from "@mui/material";
import {ShopModule} from "../../../types/shopModuleTypes";
import {useAppDispatch} from "../../../hooks/StoreHooks";
import {handleRequest} from "../../../store/utils/handleRequest";


interface Props {
    action: InAppNotificationAction;
    onChange: (action: InAppNotificationAction) => void;
    isCompanyWideAssigned: boolean;
    groupIds: number[];
    employeeUserIds: string[];
}

export default function ShopModuleReferenceField({action, onChange, isCompanyWideAssigned, groupIds, employeeUserIds,
}: Props) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [modules, setModules] = useState<ShopModule[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchModules = async () => {
            setIsLoading(true);
            const result = await handleRequest(
                dispatch,
                "indirect",
                () => shopModulesApi.getShopModulesFilteredByVisibility(isCompanyWideAssigned, groupIds, employeeUserIds)
            );
            if (result.success()) {
                const fetched = result.result ?? [];
                setModules(fetched);
                if (action.reference && !fetched.some(m => String(m.id) === action.reference)) {
                    onChange({ ...action, reference: undefined });
                }
            }
            setIsLoading(false);
        };
        void fetchModules();
    }, [isCompanyWideAssigned, groupIds, employeeUserIds, dispatch]);

    const selectedModule = modules.find(m => String(m.id) === action.reference) ?? null;

    return (
        <Autocomplete
            options={modules}
            loading={isLoading}
            value={selectedModule}
            getOptionLabel={(m) => m.type ?? String(m.id)}
            renderOption={(props, m) => (
                <li {...props}>
                    <Stack
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            alignItems: "center",
                        }}
                    >
                        <span>{m.type ?? String(m.id)}</span>
                        <Typography variant="caption" color="text.secondary">
                            {m.type}
                        </Typography>
                    </Stack>
                </li>
            )}
            onChange={(_, selected) => onChange({
                ...action,
                reference: selected ? String(selected.id) : undefined
            })}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={t("genericInAppNotificationsPage.action.reference.shopModule")}
                    slotProps={{
                        ...params.slotProps,
                        input: {
                            ...params.slotProps.input,
                            endAdornment: (
                                <>
                                    {isLoading && <CircularProgress size={18} />}
                                    {params.slotProps.input?.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
        />
    );
}